import React, { useContext, useRef, useEffect } from "react"
import AppContext from "../../shared/AppContext"
import { Query } from "react-apollo"
import DemoProductCard from "./components/DemoProductCard"
import gql from "graphql-tag"
import { Redirect } from "react-router-dom"
import Loading from "../../components/Loading"
import Video from "./components/Video"
import { Helmet } from "react-helmet"
import { defaultLogo } from "../../shared/images"
import { MetaData, structuredPageData } from "../../shared/structureData"

import "./style.scss"

const getDemoProductsQuery = gql`
  query getDemoProducts($accessToken: String!) {
    getDemoProducts(accessToken: $accessToken) {
      imageURL
      product {
        id
        name
        metadata
        proprietaryDataset
      }
    }
  }
`
const PageSEO = () => {
  let seo = {}
  seo.type = "website"
  seo.url = "https://app.etr.ai/interactive-demo"
  seo.title = `Interactive Demo | ETR`
  seo.image = defaultLogo
  seo.description =
    "ETR Interactive Demo page is an educational resource to familiarize yourself with ETR, 6 quick videos highlighting ETR's extensive offering, found all in one platform. 4 comprehensive research reports that combine data alongside human insights and analysis."

  return (
    <Helmet>
      {MetaData(seo)}
      <script type="application/ld+json">{structuredPageData(seo.url)}</script>
    </Helmet>
  )
}
const InteractiveDemo = (props) => {
  const { trackEvent } = useContext(AppContext)
  const scrollRefs = useRef([])

  useEffect(() => {
    try {
      window.analytics.identify(props.data.email, {
        email: props.data.email,
      })
    } catch (err) {
      console.log(err)
    }
  }, [])

  const componentAnalytics = (properties) => {
    trackEvent("Interactive Demo", properties)
  }

  const scrollToElem = (i) => (e) => {
    e.preventDefault()
    const { scrollLink } = e.target.dataset
    scrollRefs.current[i].scrollIntoView({
      behavior: "smooth",
    })
    componentAnalytics({
      element: "# link",
      section: "Intro content bullet point",
      action: `Scroll to ${scrollLink}`,
    })
  }

  const getDate = new Date()

  return (
    <main className="interactive-demo">
      <PageSEO />
      {/* === intro === */}
      <section className="interactive-demo-intro">
        <div className="main-content">
          <h1 className="intro-title">
            <img src="/static/images/ETR-only-color.png" /> Interactive Demo
          </h1>
          <div className="intro-content-wrap">
            <p>
              Welcome! This page is an educational resource to familiarize
              yourself with ETR. Below you will find:
            </p>
            <p>
              <a
                href="#video"
                data-scroll-link="video highlights"
                onClick={scrollToElem(0)}
              >
                <strong className="bullet-bar">6</strong> quick videos
              </a>
              {
                " highlighting ETR's extensive offering, found all in one platform"
              }
            </p>
            <p>
              <a
                href="#reports"
                data-scroll-link="research reports"
                onClick={scrollToElem(1)}
              >
                <strong className="bullet-bar sm">4</strong> comprehensive
                research reports
              </a>{" "}
              that combine data alongside human insights and analysis
            </p>
          </div>
        </div>
      </section>
      {/* === video ===  */}
      <section className="interactive-demo-video">
        <Video ref={(ref) => (scrollRefs.current[0] = ref)} />
      </section>
      {/* === reports === */}
      <section
        ref={(ref) => (scrollRefs.current[1] = ref)}
        id="reports"
        className="interactive-demo-reports"
      >
        <div className="reports-wrap main-content">
          <div className="reports-intro">
            <p>
              To familiarize yourself with ETR, we have provided access to 4
              examples of our reports:
            </p>
            <ul>
              <li>
                The first 2 focus on results from our forward-looking,
                proprietary spending intentions studies, summarizing the key
                topics and trends
              </li>
              <li>
                The Drill Down report focuses on a logic-driven survey we
                conducted with a targeted group from within our ETR Community of
                technology decision makers
              </li>
              <li>
                The Insights report showcases a moderated discussion conducted
                with one of our ETR Community members providing feedback on the
                current and future state of the Data Analytics industry
              </li>
            </ul>
          </div>
          <Query
            query={getDemoProductsQuery}
            variables={{ accessToken: props.accessToken }}
          >
            {({ data, error, loading }) => {
              if (loading) return <Loading />
              if (error) return "There was a problem loading the reports..."
              if (
                !data ||
                !data.getDemoProducts ||
                !data.getDemoProducts.length
              ) {
                return null
              }

              // group the reports by dataset so we can style them with separators
              const demoProducts = data.getDemoProducts.reduce(
                (obj, demoProduct) => {
                  let key = demoProduct.product.proprietaryDataset.toLowerCase()
                  if (!obj[key]) {
                    obj[key] = []
                  }
                  obj[key].push(demoProduct)
                  return obj
                },
                {}
              )

              let dataReports = []
              let otherDrilldowns = []

              if (demoProducts.dd) {
                for (const demoProduct of demoProducts.dd) {
                  demoProduct.product.name.toLowerCase().includes("data report")
                    ? dataReports.push(demoProduct)
                    : otherDrilldowns.push(demoProduct)
                }
              }

              return (
                <div className="demo-reports-container" data-cy="demo-reports">
                  <ul className="demo-reports tsis">
                    {[...(demoProducts.tsis || []), ...dataReports].map(
                      (demoProduct, idx) => {
                        return (
                          <DemoProductCard
                            key={`${idx}-tsis`}
                            product={demoProduct.product}
                            imageURL={demoProduct.imageURL}
                            href={`/interactive-demo/reports/${demoProduct.product.id}?token=${props.accessToken}`}
                          />
                        )
                      }
                    )}
                  </ul>
                  <ul className="demo-reports drilldown-insights">
                    {[
                      ...(otherDrilldowns || []),
                      ...(demoProducts.venn || []),
                    ].map((demoProduct, idx) => {
                      return (
                        <DemoProductCard
                          key={idx}
                          product={demoProduct.product}
                          imageURL={demoProduct.imageURL}
                          href={`/interactive-demo/reports/${demoProduct.product.id}?token=${props.accessToken}`}
                        />
                      )
                    })}
                  </ul>
                </div>
              )
            }}
          </Query>
          <div className="demo-reports-end">
            <p>
              Our full library consists of macro, sector-wide, and
              vendor-specific research reports written by our expert data and
              content teams.
            </p>
          </div>
        </div>
      </section>
      {/* === footer === */}
      <footer className="interactive-demo-footer">
        <a
          onClick={() =>
            componentAnalytics({
              section: "footer",
              element: "Image link",
              url: "https://etr.ai",
            })
          }
          href="https://etr.ai"
        >
          <img
            src="/static/images/ETR-extended-lower-white.png"
            width="150px"
            height="75px"
          />
        </a>
        <div className="social-media-links">
          <a
            onClick={() =>
              componentAnalytics({
                section: "footer",
                element: "LinkedIn link",
              })
            }
            className="link"
            href="https://www.linkedin.com/company/enterprise-technology-research/"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fab fa-linkedin"></i>
          </a>
          <a
            onClick={() =>
              componentAnalytics({
                section: "footer",
                element: "Twitter link",
              })
            }
            className="link"
            href="https://twitter.com/ETRnews"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fab fa-twitter"></i>
          </a>
        </div>
        <div className="site-links">
          <a
            onClick={() =>
              componentAnalytics({
                section: "footer",
                element: "text link",
                text: "Home",
                url: "https://etr.ai",
              })
            }
            className="link"
            href="https://etr.ai"
          >
            Home
          </a>
          <a
            onClick={() =>
              componentAnalytics({
                section: "footer",
                element: "text link",
                text: "Terms of Use",
                url: "https://etr.ai/term-of-use",
              })
            }
            className="link"
            href="https://etr.ai/term-of-use"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Use
          </a>
          <a
            onClick={() =>
              componentAnalytics({
                section: "footer",
                element: "text link",
                text: "Privacy policy",
                url: "https://etr.ai/privacy-policy",
              })
            }
            className="link"
            href="https://etr.ai/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </div>
        <p className="policy-info">
          ETR adheres to GDPR, PCI and MNPI policies and all data is anonymized
          for our clients and users.
        </p>
        <p className="copyright">
          Copyright {getDate.getFullYear()} © All rights reserved. Enterprise
          Technology Research.
        </p>
      </footer>
    </main>
  )
}

const getDemoAccessQuery = gql`
  query getDemoAccess($accessToken: String!) {
    getDemoAccess(accessToken: $accessToken) {
      id
      email
      firstName
      lastName
      isActive
    }
  }
`
const InteractiveDemoWithData = (props) => {
  const { search } = props.location
  const queryParams = new URLSearchParams(search)
  const token = queryParams.get("token")

  if (!token) {
    return <Redirect to="/" />
  }

  return (
    <Query
      query={getDemoAccessQuery}
      variables={{ accessToken: token }}
      fetchPolicy="network-only"
    >
      {({ data, loading, error }) => {
        if (loading) return "loading..."
        if (error) return "An unknown error occurred"
        if (!data.getDemoAccess || !data.getDemoAccess.isActive) {
          return <Redirect to="/" />
        }
        return <InteractiveDemo accessToken={token} data={data.getDemoAccess} />
      }}
    </Query>
  )
}

export default InteractiveDemoWithData
